import { useCallback, useMemo } from "react";
import { useTranslation } from "@me8eon/translation";
import { legacyTranslations } from "@me8eon/translate_eon";

const SUPPORTED_LANGUAGE_CODES = new Set(["en", "de", "sv", "ro", "cz", "pl", "it", "hu", "nl"]);

const normalizeLanguageCode = (value?: string | null): string | undefined => {
    if (!value) return undefined;
    const normalized = value.toLowerCase().slice(0, 2);
    if (normalized === "cs") return "cz";
    return SUPPORTED_LANGUAGE_CODES.has(normalized) ? normalized : undefined;
};

const browserLanguageCode = (): string | undefined => {
    if (typeof navigator === "undefined") return undefined;
    return normalizeLanguageCode(navigator.language || (navigator as any).userLanguage);
};

const getTranslationValue = (language: string, key: string): string | undefined => {
    const languageTranslations = (legacyTranslations as Record<string, any>)[language];
    const translationNamespace = languageTranslations?.translation as Record<string, any> | undefined;
    if (!translationNamespace) return undefined;

    const resolved = key.split(".").reduce<any>((acc, part) => {
        if (acc && typeof acc === "object" && part in acc) return acc[part];
        return undefined;
    }, translationNamespace);

    return typeof resolved === "string" ? resolved : undefined;
};

export const useLocalTranslation = (queryLanguage?: string) => {
    const t = useTranslation();

    const effectiveLanguage = useMemo(
        () =>
            normalizeLanguageCode(queryLanguage) ||
            browserLanguageCode() ||
            normalizeLanguageCode(t.i18n.language) ||
            "en",
        [queryLanguage, t]
    );

    const localT = useCallback((key: string) => {
        return getTranslationValue(effectiveLanguage, key) || t(key);
    }, [effectiveLanguage, t]);

    return {
        localT,
        effectiveLanguage,
    };
};
